import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Users, Plus, Trash2, Link2, Copy, Check, AlertCircle,
  RefreshCw, Mail, Phone, MapPin, User, Wrench,
  Upload, Calendar, FileText, Clock, XCircle, CheckCircle, ScanLine, Info, ShieldAlert,
  Save, X, ChevronDown, ChevronUp,
} from 'lucide-react';
import { useAppAuth } from '../hooks/useAppAuth';
import { createApiClient, profileService, carrierEmployeesService, workshopEmployeesService, documentsService } from '../services/api';
import { DocumentType } from '../types';
import type { CarrierEmployee, WorkshopEmployee, DocumentUpload } from '../types';

type Employee = CarrierEmployee | WorkshopEmployee;

// ─── Driving Licence upload + AI scan ────────────────────────────────────────

interface DlScanResult {
  firstName: string | null;
  lastName: string | null;
  dateOfBirth: string | null;
  licenceNumber: string | null;
  address: string | null;
  expiryDate: string | null;
  nationality: string | null;
  licenceCategories: string | null;
  confidence: 'Low' | 'Medium' | 'High';
  note: string | null;
}

function DlScanPanel({ file, getToken }: { file: File; getToken: () => Promise<string> }) {
  const [scanning, setScanning]   = useState(false);
  const [result, setResult]       = useState<DlScanResult | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);

  const CONFIDENCE_COLOUR = {
    High:   'text-green-700 bg-green-50 border-green-200',
    Medium: 'text-yellow-700 bg-yellow-50 border-yellow-200',
    Low:    'text-red-700 bg-red-50 border-red-200',
  };

  const scan = async () => {
    setScanning(true); setScanError(null); setResult(null);
    try {
      const form = new FormData();
      form.append('image', file);
      const res = await createApiClient(getToken).post<DlScanResult>(
        '/api/v1/identity/driving-licence/scan', form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setResult(res.data);
    } catch (err: any) {
      setScanError(err?.response?.data ?? 'AI scan failed. Please check the image.');
    } finally {
      setScanning(false);
    }
  };

  return (
    <div className="mt-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold text-blue-700 flex items-center gap-1">
          <ScanLine className="w-3.5 h-3.5" />AI Scan
        </p>
        {!result ? (
          <button
            type="button" onClick={scan} disabled={scanning}
            className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
          >
            {scanning
              ? <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
              : <ScanLine className="w-3 h-3" />
            }
            {scanning ? 'Scanning…' : 'Extract details'}
          </button>
        ) : (
          <button type="button" onClick={scan} disabled={scanning} className="text-xs text-blue-600 hover:underline">
            Re-scan
          </button>
        )}
      </div>
      {scanError && (
        <p className="text-xs text-red-600 flex items-center gap-1">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />{scanError}
        </p>
      )}
      {result && (
        <div className="space-y-1.5">
          <div className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded border ${CONFIDENCE_COLOUR[result.confidence]}`}>
            <Info className="w-3 h-3" />Confidence: {result.confidence}
          </div>
          {result.note && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">{result.note}</p>
          )}
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            {([
              ['Name',          [result.firstName, result.lastName].filter(Boolean).join(' ')],
              ['Date of Birth', result.dateOfBirth],
              ['Licence No.',   result.licenceNumber],
              ['Expiry',        result.expiryDate],
              ['Categories',    result.licenceCategories],
              ['Nationality',   result.nationality],
            ] as [string, string | null][]).map(([label, value]) =>
              value ? (
                <div key={label}>
                  <span className="text-gray-400">{label}: </span>
                  <span className="font-medium text-gray-700">{value}</span>
                </div>
              ) : null
            )}
          </div>
          {result.address && (
            <div className="text-xs">
              <span className="text-gray-400">Address: </span>
              <span className="font-medium text-gray-700">{result.address}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DrivingLicenceCard({ getToken }: { getToken: () => Promise<string> }) {
  const [file, setFile]           = useState<File | null>(null);
  const [expiryDate, setExpiry]   = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [success, setSuccess]     = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: uploads = [], refetch } = useQuery<DocumentUpload[]>({
    queryKey: ['my-documents'],
    queryFn: () => documentsService(createApiClient(getToken)).getMy(),
  });

  const dlUploads = [...uploads]
    .filter(u => u.documentType === DocumentType.DrivingLicence)
    .sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime());
  const latest  = dlUploads[0];
  const history = dlUploads.slice(1);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) { setError('Please select a file.'); return; }
    setUploading(true); setError(null); setSuccess(false);
    try {
      await documentsService(createApiClient(getToken)).upload(file, DocumentType.DrivingLicence, 'Carrier', expiryDate || undefined);
      setSuccess(true); setFile(null); setExpiry('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      refetch();
    } catch (err: any) {
      setError(err?.response?.data?.title ?? err?.message ?? 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const statusBadge = (status: string) => {
    if (status === 'Approved') return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 shrink-0">
        <CheckCircle className="w-3 h-3" />Approved
      </span>
    );
    if (status === 'Rejected') return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 shrink-0">
        <XCircle className="w-3 h-3" />Rejected
      </span>
    );
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 shrink-0">
        <Clock className="w-3 h-3" />Pending Review
      </span>
    );
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-5 py-4 flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 text-sm">My Driving Licence</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Upload both sides of your DVLA photocard licence (image only — no PDF).
          </p>
        </div>
        {latest ? statusBadge(latest.reviewStatus) : <span className="text-xs text-gray-400 shrink-0">Not submitted</span>}
      </div>

      {latest && (
        <div className={`px-5 py-2.5 border-t border-b text-xs flex flex-col gap-1 ${latest.reviewStatus === 'Rejected' ? 'bg-red-50 border-red-100' : 'bg-gray-50 border-gray-100'}`}>
          <div className="flex items-center gap-2 text-gray-600">
            <FileText className="w-3.5 h-3.5 shrink-0 text-gray-400" />
            <span className="truncate font-medium">{latest.originalFileName}</span>
            <span className="text-gray-400 shrink-0">· {new Date(latest.uploadedAt).toLocaleDateString('en-GB')}</span>
            {latest.expiryDate && (
              <span className="text-gray-400 shrink-0">· Expires {new Date(latest.expiryDate).toLocaleDateString('en-GB')}</span>
            )}
          </div>
          {latest.reviewStatus === 'Rejected' && latest.reviewNotes && (
            <p className="text-red-600 pl-5"><span className="font-medium">Rejected: </span>{latest.reviewNotes}</p>
          )}
          {history.length > 0 && (
            <button type="button" onClick={() => setShowHistory(h => !h)} className="text-blue-500 hover:text-blue-700 pl-5 text-left">
              {showHistory ? 'Hide' : 'Show'} {history.length} previous version{history.length > 1 ? 's' : ''}
            </button>
          )}
          {showHistory && history.map(u => (
            <div key={u.documentId} className="pl-5 flex items-center gap-2 text-gray-400">
              <FileText className="w-3 h-3 shrink-0" />
              <span className="truncate">{u.originalFileName}</span>
              <span className="shrink-0">· {new Date(u.uploadedAt).toLocaleDateString('en-GB')}</span>
              {statusBadge(u.reviewStatus)}
            </div>
          ))}
        </div>
      )}

      <form onSubmit={handleSubmit} className="px-5 py-4 space-y-3">
        {latest && !success && (
          <p className="text-xs text-gray-500 font-medium">
            {latest.reviewStatus === 'Rejected' ? 'Upload a corrected document:' : 'Upload a replacement:'}
          </p>
        )}
        <div className="flex gap-2 items-center flex-wrap sm:flex-nowrap">
          <div
            className="flex-1 min-w-0 border border-dashed border-gray-300 rounded-lg px-3 py-2 text-xs cursor-pointer hover:border-blue-400 transition-colors flex items-center gap-2"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            {file
              ? <span className="text-gray-700 font-medium truncate">{file.name}</span>
              : <span className="text-gray-400">{latest ? 'Choose replacement file' : 'Choose file to upload'}</span>
            }
          </div>
          <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
            onChange={e => { setFile(e.target.files?.[0] ?? null); setError(null); setSuccess(false); }} />
          <div className="shrink-0 w-36">
            <label className="block text-xs text-gray-500 mb-1">Expiry Date</label>
            <div className="relative">
              <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
              <input type="date" value={expiryDate} onChange={e => setExpiry(e.target.value)}
                className="w-full border border-gray-300 rounded-lg pl-6 pr-2 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
          </div>
          <button type="submit" disabled={uploading || !file}
            className="shrink-0 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors flex items-center gap-1.5"
          >
            {uploading
              ? <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
              : <Upload className="w-3 h-3" />
            }
            {uploading ? 'Uploading…' : 'Save'}
          </button>
        </div>
        {file && <DlScanPanel file={file} getToken={getToken} />}
        {error && (
          <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{error}
          </div>
        )}
        {success && (
          <div className="flex items-start gap-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
            <CheckCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />Submitted for verification.
          </div>
        )}
      </form>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function isCarrierEmployee(employee: Employee): employee is CarrierEmployee {
  return 'portalLinkActive' in employee;
}

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text).catch(() => {
    const el = document.createElement('textarea');
    el.value = text;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
  });
}

function expiryCell(dateStr: string | null | undefined) {
  if (!dateStr) return <span className="text-gray-400 text-xs">—</span>;
  const days = Math.ceil((new Date(dateStr).getTime() - Date.now()) / 86_400_000);
  const cls  = days < 0 ? 'text-red-600' : days < 30 ? 'text-red-500' : days < 90 ? 'text-amber-600' : 'text-green-700';
  return (
    <span className={`text-xs font-medium ${cls} flex items-center gap-1`}>
      {days < 0 && <ShieldAlert className="w-3 h-3 shrink-0" />}
      {new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })}
    </span>
  );
}

// ─── Employee row ─────────────────────────────────────────────────────────────

function EmployeeRow({
  employee,
  isSelected,
  onSelect,
  onGenerateLink,
  onRevokeLink,
  generating,
  showExpiry,
  isSelf,
  isOwnerRow,
}: {
  employee: Employee;
  isSelected: boolean;
  onSelect: () => void;
  onGenerateLink?: () => void;
  onRevokeLink?: () => void;
  generating: boolean;
  showExpiry: boolean;
  isSelf: boolean;
  isOwnerRow: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const carrier = isCarrierEmployee(employee);

  const handleCopyMagicLink = () => {
    if (!carrier || !employee.portalLinkUrl) return;
    copyToClipboard(employee.portalLinkUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const driveLinkCell = () => {
    // Owner (self row): no portal link needed, they already have full account access.
    if (isSelf || isOwnerRow) return <span className="text-gray-400 text-xs">—</span>;

    // Non-owner employee: magic link
    if (!carrier) return <span className="text-sm text-gray-500">—</span>;
    if (employee.portalLinkActive && employee.portalLinkUrl) {
      return (
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={handleCopyMagicLink}
            className="flex items-center gap-1 px-2 py-1 text-xs rounded-lg bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 transition-colors"
          >
            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            {copied ? 'Copied!' : 'Copy Link'}
          </button>
          <button
            onClick={onRevokeLink}
            disabled={generating}
            className="flex items-center gap-1 px-2 py-1 text-xs rounded-lg bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors disabled:opacity-50"
          >
            <RefreshCw className="w-3 h-3" /> Revoke
          </button>
        </div>
      );
    }
    return (
      <button
        onClick={onGenerateLink}
        disabled={generating}
        className="flex items-center gap-1 px-2 py-1 text-xs rounded-lg bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors disabled:opacity-50"
      >
        <Link2 className="w-3 h-3" />
        {generating ? 'Generating…' : 'Generate Link'}
      </button>
    );
  };

  return (
    <tr
      className={`border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer ${isSelected ? 'bg-blue-50/40' : ''}`}
      onClick={onSelect}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${isSelf ? 'bg-blue-600' : 'bg-blue-100'}`}>
            <User className={`w-4 h-4 ${isSelf ? 'text-white' : 'text-blue-600'}`} />
          </div>
          <div>
            <span className="font-semibold text-gray-900 text-sm">{employee.fullName}</span>
            {isSelf && <span className="ml-2 text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full">You</span>}
            {isOwnerRow && <span className="ml-2 text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full">Owner</span>}
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">
        <div className="flex items-center gap-1.5">
          <Mail className="w-3.5 h-3.5 text-gray-400" />
          {employee.email}
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">
        {employee.mobile
          ? <div className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-gray-400" />{employee.mobile}</div>
          : <span className="text-gray-400">—</span>
        }
      </td>
      <td className="px-4 py-3 text-sm text-gray-600 max-w-[160px]">
        {employee.address
          ? <div className="flex items-center gap-1.5 truncate"><MapPin className="w-3.5 h-3.5 text-gray-400 shrink-0" /><span className="truncate">{employee.address}</span></div>
          : <span className="text-gray-400">—</span>
        }
      </td>
      {showExpiry && (
        <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
          {carrier && !isSelf
            ? expiryCell(employee.licenceExpiryDate)
            : <span className="text-gray-400 text-xs">—</span>
          }
        </td>
      )}
      <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
        {driveLinkCell()}
      </td>
      <td className="px-4 py-3 text-right">
        {isSelected
          ? <ChevronUp className="w-4 h-4 text-blue-400" />
          : <ChevronDown className="w-4 h-4 text-gray-300" />
        }
      </td>
    </tr>
  );
}

// ─── Inline edit panel ────────────────────────────────────────────────────────

function EmployeeEditPanel({
  employee,
  isSelf,
  getToken,
  colSpan,
  onSave,
  onCancel,
  onDelete,
  saving,
  deleting,
  error,
}: {
  employee: Employee;
  isSelf: boolean;
  getToken: () => Promise<string>;
  colSpan: number;
  onSave: (data: { fullName: string; mobile: string; address: string; licExpiry: string }) => void;
  onCancel: () => void;
  onDelete?: () => void;
  saving: boolean;
  deleting?: boolean;
  error: string | null;
}) {
  const carrier = isCarrierEmployee(employee);

  const [fullName,  setFullName]  = useState(employee.fullName);
  const [mobile,    setMobile]    = useState(employee.mobile ?? '');
  const [address,   setAddress]   = useState(employee.address ?? '');
  const [licExpiry, setLicExpiry] = useState(carrier && !isSelf ? (employee.licenceExpiryDate?.split('T')[0] ?? '') : '');

  return (
    <tr className="bg-blue-50/30 border-b border-blue-100">
      <td colSpan={colSpan} className="px-6 py-4">
        <div className="space-y-4">
          {/* Edit fields — hidden for self (profile manages their own details) */}
          {!isSelf && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Full Name</label>
                <input
                  value={fullName}
                  onChange={e => setFullName(e.target.value)}
                  className="form-input text-sm"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                <input
                  value={employee.email}
                  disabled
                  className="form-input text-sm bg-gray-50 text-gray-400 cursor-not-allowed"
                  title="Email cannot be changed"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Mobile</label>
                <input
                  value={mobile}
                  onChange={e => setMobile(e.target.value)}
                  className="form-input text-sm"
                  placeholder="+44 7700 000000"
                />
              </div>
              <div className="sm:col-span-2 lg:col-span-3">
                <label className="block text-xs font-medium text-gray-600 mb-1">Address</label>
                <input
                  value={address}
                  onChange={e => setAddress(e.target.value)}
                  className="form-input text-sm"
                  placeholder="123 High St, London"
                />
              </div>
              {carrier && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Driving Licence Expiry
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                    <input type="date" value={licExpiry} onChange={e => setLicExpiry(e.target.value)}
                      className="form-input pl-8 text-sm" />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Save / Cancel / Delete */}
          {!isSelf && (
            <>
              {error && (
                <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{error}
                </div>
              )}
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => onSave({ fullName, mobile, address, licExpiry })}
                    disabled={saving || !fullName.trim()}
                    className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors"
                  >
                    {saving
                      ? <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      : <Save className="w-3 h-3" />
                    }
                    {saving ? 'Saving…' : 'Save Changes'}
                  </button>
                  <button
                    type="button"
                    onClick={onCancel}
                    className="flex items-center gap-1.5 px-4 py-2 text-xs text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-3 h-3" /> Cancel
                  </button>
                </div>
                {onDelete && (
                  <button
                    type="button"
                    onClick={onDelete}
                    disabled={deleting}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs text-red-600 hover:bg-red-50 border border-red-200 rounded-lg transition-colors disabled:opacity-40"
                  >
                    {deleting
                      ? <span className="w-3 h-3 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                      : <Trash2 className="w-3 h-3" />
                    }
                    {deleting ? 'Removing…' : 'Remove Employee'}
                  </button>
                )}
              </div>
            </>
          )}

          {/* Driving licence upload — only for self row */}
          {isSelf && (
            <div className="space-y-2">
              <p className="text-xs text-gray-500">
                Your profile details are managed on the <strong>Profile</strong> page.
                Upload your driving licence here for verification:
              </p>
              <DrivingLicenceCard getToken={getToken} />
              <button
                type="button"
                onClick={onCancel}
                className="flex items-center gap-1.5 px-4 py-2 text-xs text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-3 h-3" /> Close
              </button>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export default function EmployeesPage() {
  const { getToken, account } = useAppAuth();
  const qc = useQueryClient();

  const api = createApiClient(getToken);
  const carrierEmpSvc = carrierEmployeesService(api);
  const workshopEmpSvc = workshopEmployeesService(api);
  const profSvc = profileService(api);

  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: () => profSvc.getProfile(),
    enabled: !!account,
  });

  const isWorkshop = profile?.role?.toLowerCase() === 'workshop';
  const empSvc = isWorkshop ? null : carrierEmpSvc;
  const isSingleDriver = !profile?.isBusinessAccount;
  const employeeLabel = isWorkshop ? 'Mechanic' : 'Driver';
  const employeeLabelPlural = isWorkshop ? 'Mechanics' : 'Drivers';

  const queryKey = isWorkshop ? 'workshop-employees' : 'carrier-employees';

  const { data: employees = [], isLoading } = useQuery<Employee[], Error>({
    queryKey: [queryKey],
    queryFn: async () => isWorkshop
      ? await workshopEmpSvc.getAll()
      : await carrierEmpSvc.getAll(),
    enabled: !!account && !!profile,
  });

  // ── Add form state ──
  const [fullName, setFullName]               = useState('');
  const [email, setEmail]                     = useState('');
  const [mobile, setMobile]                   = useState('');
  const [address, setAddress]                 = useState('');
  const [licenceExpiry, setLicenceExpiry] = useState('');
  const [formError, setFormError]             = useState<string | null>(null);
  const [showForm, setShowForm]               = useState(false);

  // ── Selected row state ──
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editError, setEditError]   = useState<string | null>(null);

  // ── Newly generated employee links (captured at generation time; list endpoint doesn't re-serve the token) ──
  const [newlyGeneratedLinks, setNewlyGeneratedLinks] = useState<Record<string, string>>({});

  // ── Mutations ──
  const addMut = useMutation<Employee, Error, void>({
    mutationFn: async () => {
      const emp = isWorkshop
        ? await workshopEmpSvc.add({ fullName: fullName.trim(), email: email.trim(), mobile: mobile.trim() || undefined, address: address.trim() || undefined })
        : await carrierEmpSvc.add({ fullName: fullName.trim(), email: email.trim(), mobile: mobile.trim() || undefined, address: address.trim() || undefined });
      if (!isWorkshop && licenceExpiry) {
        await carrierEmpSvc.updateExpiry(emp.id, null, licenceExpiry || null);
      }
      return emp;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [queryKey] });
      setFullName(''); setEmail(''); setMobile(''); setAddress('');
      setLicenceExpiry('');
      setFormError(null);
      setShowForm(false);
    },
    onError: (err: Error) => setFormError(err.message),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => isWorkshop ? workshopEmpSvc.remove(id) : carrierEmpSvc.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [queryKey] }),
  });

  const linkMut = useMutation({
    mutationFn: (id: string) => carrierEmpSvc.generateMagicLink(id),
    onSuccess: (data, id) => {
      setNewlyGeneratedLinks(prev => ({ ...prev, [id]: data.url }));
      qc.invalidateQueries({ queryKey: [queryKey] });
    },
  });

  const revokeMut = useMutation({
    mutationFn: (id: string) => carrierEmpSvc.revokeMagicLink(id),
    onSuccess: (_data, id) => {
      setNewlyGeneratedLinks(prev => { const next = { ...prev }; delete next[id]; return next; });
      qc.invalidateQueries({ queryKey: [queryKey] });
    },
  });

  const updateMut = useMutation({
    mutationFn: async ({ id, data }: {
      id: string;
      data: { fullName: string; mobile: string; address: string; licExpiry: string };
    }) => {
      await carrierEmpSvc.updateEmployee(id, {
        fullName: data.fullName.trim(),
        mobile:   data.mobile.trim() || undefined,
        address:  data.address.trim() || undefined,
      });
      await carrierEmpSvc.updateExpiry(id, null, data.licExpiry || null);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [queryKey] });
      setSelectedId(null);
      setEditError(null);
    },
    onError: (err: Error) => setEditError(err.message),
  });

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName.trim() || !email.trim()) {
      setFormError('Full name and email are required.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setFormError('Please enter a valid email address.');
      return;
    }
    setFormError(null);
    addMut.mutate();
  };

  const handleRowSelect = (empId: string) => {
    setSelectedId(prev => prev === empId ? null : empId);
    setEditError(null);
  };

  // Prepend a synthetic self-entry for the carrier owner so the "You" badge and DL upload panel appear.
  const selfEntry: CarrierEmployee | null = !isWorkshop && profile ? {
    id: 'self',
    fullName: `${profile.firstName} ${profile.lastName}`.trim(),
    email: profile.email,
    mobile: profile.phoneNumber || undefined,
    isOwner: true,
    portalLinkActive: false,
    createdAt: new Date().toISOString(),
  } : null;

  // Exclude the owner's own record from the employee list — the synthetic self entry above
  // already represents them, and they don't need a portal magic link of their own.
  const displayEmployees: (CarrierEmployee | WorkshopEmployee)[] = selfEntry
    ? [selfEntry, ...(employees as CarrierEmployee[]).filter(e => !e.isOwner)]
    : employees as (CarrierEmployee | WorkshopEmployee)[];

  // Col count for colSpan calculations
  const colCount = isWorkshop ? 5 : 7;

  return (
    <div className="page-shell space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            {isWorkshop ? <Wrench className="w-5 h-5 text-blue-600" /> : <Users className="w-5 h-5 text-blue-600" />}
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{employeeLabelPlural}</h1>
            <p className="text-sm text-gray-500">
              {isSingleDriver
                ? `Your ${employeeLabel.toLowerCase()} profile and team members.`
                : `Manage your ${employeeLabelPlural.toLowerCase()} and team members.`}
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowForm(v => !v)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add {employeeLabel}
        </button>
      </div>

      {/* Add form */}
      {showForm && (
        <form onSubmit={handleAdd} className="card p-5 space-y-4">
          <h2 className="font-semibold text-gray-800 text-sm flex items-center gap-2">
            <Plus className="w-4 h-4 text-blue-500" /> Add New {employeeLabel}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name <span className="text-red-500">*</span></label>
              <input value={fullName} onChange={e => setFullName(e.target.value)} className="form-input" placeholder="John Smith" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email <span className="text-red-500">*</span></label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="form-input" placeholder="john@example.com" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Mobile</label>
              <input value={mobile} onChange={e => setMobile(e.target.value)} className="form-input" placeholder="+44 7700 000000" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Address</label>
              <input value={address} onChange={e => setAddress(e.target.value)} className="form-input" placeholder="123 High St, London" />
            </div>
            {!isWorkshop && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Licence Expiry <span className="text-gray-400 font-normal">(optional)</span></label>
                <div className="relative">
                  <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                  <input type="date" value={licenceExpiry} onChange={e => setLicenceExpiry(e.target.value)} className="form-input pl-8" />
                </div>
              </div>
            )}
          </div>

          {formError && (
            <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />{formError}
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={addMut.isPending}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm"
            >
              {addMut.isPending
                ? <><span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />Saving…</>
                : <><Plus className="w-4 h-4" />Add {employeeLabel}</>
              }
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin w-6 h-6 border-4 border-blue-500 border-t-transparent rounded-full" />
          </div>
        ) : displayEmployees.length === 0 ? (
          <div className="py-12 text-center text-gray-400">
            <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">No {employeeLabelPlural.toLowerCase()} yet</p>
            <p className="text-xs mt-1">Add team members to assign them to jobs.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Mobile</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Address</th>
                  {!isWorkshop && <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Expiry</th>}
                  {!isWorkshop && <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Drive Link</th>}
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {displayEmployees.map(emp => {
                  const isSelf    = emp.id === 'self';
                  const isOwnerRow = !isSelf && emp.isOwner;
                  const isSelected = selectedId === emp.id;
                  // Merge locally-captured URL so the Copy Link button shows immediately after generation.
                  // The list endpoint doesn't re-serve the token for security reasons.
                  const effectiveEmp = isCarrierEmployee(emp) && newlyGeneratedLinks[emp.id]
                    ? { ...emp, portalLinkActive: true, portalLinkUrl: newlyGeneratedLinks[emp.id] }
                    : emp;

                  return (
                    <>
                      <EmployeeRow
                        key={emp.id}
                        employee={effectiveEmp}
                        isSelected={isSelected}
                        onSelect={() => handleRowSelect(emp.id)}
                        onGenerateLink={() => linkMut.mutate(emp.id)}
                        onRevokeLink={() => revokeMut.mutate(emp.id)}
                        generating={linkMut.isPending && linkMut.variables === emp.id}
                        showExpiry={!isWorkshop}
                        isSelf={isSelf}
                        isOwnerRow={isOwnerRow}
                      />
                      {isSelected && (
                        <EmployeeEditPanel
                          key={`${emp.id}-edit`}
                          employee={effectiveEmp}
                          isSelf={isSelf}
                          getToken={getToken}
                          colSpan={colCount}
                          onSave={data => updateMut.mutate({ id: emp.id, data })}
                          onCancel={() => setSelectedId(null)}
                          onDelete={!isSelf ? () => { deleteMut.mutate(emp.id); setSelectedId(null); } : undefined}
                          saving={updateMut.isPending}
                          deleting={deleteMut.isPending && deleteMut.variables === emp.id}
                          error={editError}
                        />
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info */}
      {isWorkshop ? (
        <div className="card p-4 bg-blue-50 border-blue-100 text-xs text-blue-800 space-y-1">
          <p className="font-semibold">About Workshop Mechanics</p>
          <p>Add your mechanics here. As a 1-man workshop you are automatically your own first mechanic.</p>
          <p>Mechanics can be assigned to repair jobs via the job assignment flow.</p>
        </div>
      ) : (
        <div className="card p-4 bg-blue-50 border-blue-100 text-xs text-blue-800 space-y-1">
          <p className="font-semibold">About Portal Links</p>
          <p>A portal link lets an employee access the portal for their assigned jobs without needing a TransitDeal account.</p>
          <p>Click any employee row to edit their details or update expiry dates. Click your own row to manage your driving licence.</p>
        </div>
      )}
    </div>
  );
}
